import { createServerFn } from '@tanstack/react-start'
import { getRequestHeaders } from '@tanstack/react-start/server'
import { auth } from '../lib/auth'
import { logCauseChain } from '../lib/server-debug'
import { devAdminToken, devAuthEnabled } from '../lib/dev-auth'
import { isAccessTokenActive } from '../lib/token-status'
import {
  listDevices as listDevicesApi,
  createDevice as createDeviceApi,
  sendCommand as sendCommandApi,
  revokeDevice as revokeDeviceApi,
  getDevice as getDeviceApi,
  updateDevice as updateDeviceApi,
  bulkUpdate as bulkUpdateApi,
  listEvents as listEventsApi,
  listCommands as listCommandsApi,
  listPayments as listPaymentsApi,
  listTelemetry as listTelemetryApi,
  listSites as listSitesApi,
  createSite as createSiteApi,
  updateSite as updateSiteApi,
  deleteSite as deleteSiteApi,
  listBoxes as listBoxesApi,
  replaceBoxes as replaceBoxesApi,
  syncBoxes as syncBoxesApi,
  listBoards as listBoardsApi,
  replaceBoards as replaceBoardsApi,
  listCodes as listCodesApi,
  issueCode as issueCodeApi,
  revokeCode as revokeCodeApi,
  syncCodes as syncCodesApi,
  signingKey as signingKeyApi,
} from '../api/generated/admin/admin'
import type {
  ListDevicesParams,
  ListEventsParams,
  ListCommandsParams,
  ListPaymentsParams,
  ListSitesParams,
  ListTelemetryParams,
  CreateRequest,
  CommandRequest,
  RevokeRequest,
  UpdateRequest,
  BulkUpdateRequest,
  SiteView,
  SiteListResponse,
  BoxesRequest,
  BoardsRequest,
  IssueCodeRequest,
} from '#/api/generated/schemas'

async function authHeaders(): Promise<HeadersInit> {
  // Dev bypass: the shared secret storage-api's dev authenticator accepts, so
  // localhost needs no Zitadel round trip. Null in production, always.
  const devToken = devAdminToken()
  if (devToken) return { Authorization: `Bearer ${devToken}` }

  const headers = getRequestHeaders()
  const { accessToken } = await auth.api.getAccessToken({
    body: { providerId: 'zitadel' },
    headers,
  })
  return { Authorization: `Bearer ${accessToken}` }
}

function unwrap<T>(name: string, res: { status: number; data: unknown }): T {
  if (res.status >= 200 && res.status < 300) return res.data as T
  const errBody = res.data as { error?: { message?: string } } | undefined
  throw new Error(errBody?.error?.message ?? `${name} failed with status ${res.status}`)
}

function withLog<A, R>(name: string, fn: (args: A) => Promise<R>): (args: A) => Promise<R> {
  return async (args) => {
    try {
      return await fn(args)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`\n[server-fn ${name}] error:`)
      logCauseChain('  ', e)
      throw e
    }
  }
}

// --- Access ---

const ADMIN_ROLES = ['admin']

/** Why access was refused — `admin.tsx` routes on this, humans read `reason`. */
export type AdminAccessDenial = 'unauthenticated' | 'token-inactive' | 'missing-role'

export type AdminAccess = {
  allowed: boolean
  denial: AdminAccessDenial | null
  reason: string | null
}

/* The session user carries the Zitadel project roles (mapped in auth.ts), so
   the role half is decided locally. The token half is not: a stateless session
   cookie outlives a revoked grant, so Zitadel gets the final say. */
export const checkAdminAccess = createServerFn({ method: 'GET' }).handler(
  async (): Promise<AdminAccess> => {
    if (devAuthEnabled()) return { allowed: true, denial: null, reason: null }
    const headers = getRequestHeaders()
    const session = await auth.api.getSession({ headers })

    if (!session) {
      return { allowed: false, denial: 'unauthenticated', reason: 'Not authenticated' }
    }

    let accessToken: string | undefined
    try {
      ;({ accessToken } = await auth.api.getAccessToken({
        body: { providerId: 'zitadel' },
        headers,
      }))
    } catch {
      accessToken = undefined
    }
    if (!(await isAccessTokenActive(accessToken))) {
      return {
        allowed: false,
        denial: 'token-inactive',
        reason: 'Your session is no longer active — sign in again',
      }
    }

    const roles = (session.user as { roles?: string[] }).roles ?? []
    if (!roles.some((r) => ADMIN_ROLES.includes(r))) {
      return {
        allowed: false,
        denial: 'missing-role',
        reason: 'Your account is missing the admin role',
      }
    }
    return { allowed: true, denial: null, reason: null }
  },
)

// --- Devices ---

export const fetchDevices = createServerFn({ method: 'GET' })
  .inputValidator((input: ListDevicesParams) => input)
  .handler(
    withLog('fetchDevices', async ({ data: params }) => {
      const headers = await authHeaders()
      const res = await listDevicesApi(params, { headers })
      return unwrap<import('../api/generated/schemas').ListResponse>('fetchDevices', res)
    }),
  )

export const fetchDevice = createServerFn({ method: 'GET' })
  .inputValidator((input: { deviceId: string }) => input)
  .handler(
    withLog('fetchDevice', async ({ data: { deviceId } }) => {
      const headers = await authHeaders()
      const res = await getDeviceApi(deviceId, { headers })
      return unwrap<import('../api/generated/schemas').DeviceView>('fetchDevice', res)
    }),
  )

export const createDevice = createServerFn({ method: 'POST' })
  .inputValidator((input: CreateRequest) => input)
  .handler(
    withLog('createDevice', async ({ data: body }) => {
      const headers = await authHeaders()
      const res = await createDeviceApi(body, { headers })
      return unwrap<import('../api/generated/schemas').CreateResponse>('createDevice', res)
    }),
  )

export const updateDevice = createServerFn({ method: 'POST' })
  .inputValidator((input: { deviceId: string; body: UpdateRequest }) => input)
  .handler(
    withLog('updateDevice', async ({ data: { deviceId, body } }) => {
      const headers = await authHeaders()
      const res = await updateDeviceApi(deviceId, body, { headers })
      return unwrap<import('../api/generated/schemas').DeviceView>('updateDevice', res)
    }),
  )

export const sendDeviceCommand = createServerFn({ method: 'POST' })
  .inputValidator((input: { deviceId: string; body: CommandRequest }) => input)
  .handler(
    withLog('sendDeviceCommand', async ({ data: { deviceId, body } }) => {
      const headers = await authHeaders()
      const res = await sendCommandApi(deviceId, body, { headers })
      return unwrap<import('../api/generated/schemas').CommandResponse>('sendDeviceCommand', res)
    }),
  )

export const revokeDevice = createServerFn({ method: 'POST' })
  .inputValidator((input: { deviceId: string; body: RevokeRequest }) => input)
  .handler(
    withLog('revokeDevice', async ({ data: { deviceId, body } }) => {
      const headers = await authHeaders()
      const res = await revokeDeviceApi(deviceId, body, { headers })
      return unwrap<import('../api/generated/schemas').DeviceView>('revokeDevice', res)
    }),
  )

/* Event payloads and payment metadata are free-form JSON maps, which the
   server-fn serializer rejects — flatten them to strings at the boundary. */

export type SerializedEvent = Omit<import('../api/generated/schemas').EventView, 'payload'> & {
  payload: string
}
export type SerializedEventsResponse = Omit<
  import('../api/generated/schemas').EventsResponse,
  'items'
> & { items: SerializedEvent[] }

export type SerializedCommand = Omit<
  import('../api/generated/schemas').CommandView,
  'payload'
> & { payload: string }
export type SerializedCommandsResponse = Omit<
  import('../api/generated/schemas').CommandsResponse,
  'items'
> & { items: SerializedCommand[] }

export type SerializedPayment = Omit<
  import('../api/generated/schemas').PaymentView,
  'metadata'
> & { metadata: string }
export type SerializedPaymentsResponse = Omit<
  import('../api/generated/schemas').PaymentsResponse,
  'items'
> & { items: SerializedPayment[] }

export const fetchDeviceEvents = createServerFn({ method: 'GET' })
  .inputValidator((input: { deviceId: string; params?: ListEventsParams }) => input)
  .handler(
    withLog('fetchDeviceEvents', async ({ data: { deviceId, params } }): Promise<SerializedEventsResponse> => {
      const headers = await authHeaders()
      const res = await listEventsApi(deviceId, params, { headers })
      const data = unwrap<import('../api/generated/schemas').EventsResponse>(
        'fetchDeviceEvents',
        res,
      )
      return {
        ...data,
        items: data.items.map((e) => ({ ...e, payload: JSON.stringify(e.payload) })),
      }
    }),
  )

export const fetchDeviceCommands = createServerFn({ method: 'GET' })
  .inputValidator((input: { deviceId: string; params?: ListCommandsParams }) => input)
  .handler(
    withLog('fetchDeviceCommands', async ({ data: { deviceId, params } }): Promise<SerializedCommandsResponse> => {
      const headers = await authHeaders()
      const res = await listCommandsApi(deviceId, params, { headers })
      const data = unwrap<import('../api/generated/schemas').CommandsResponse>(
        'fetchDeviceCommands',
        res,
      )
      return {
        ...data,
        items: data.items.map((c) => ({ ...c, payload: JSON.stringify(c.payload) })),
      }
    }),
  )

export const fetchDevicePayments = createServerFn({ method: 'GET' })
  .inputValidator((input: { deviceId: string; params?: ListPaymentsParams }) => input)
  .handler(
    withLog('fetchDevicePayments', async ({ data: { deviceId, params } }): Promise<SerializedPaymentsResponse> => {
      const headers = await authHeaders()
      const res = await listPaymentsApi(deviceId, params, { headers })
      const data = unwrap<import('../api/generated/schemas').PaymentsResponse>(
        'fetchDevicePayments',
        res,
      )
      return {
        ...data,
        items: data.items.map((p) => ({ ...p, metadata: JSON.stringify(p.metadata) })),
      }
    }),
  )

/* Telemetry rides the device's retained `state` message, so this is the
   time series behind the device charts — every metric nullable, a null
   meaning "the device could not read it". No free-form JSON in the view,
   so it crosses the server-fn boundary as-is. */
export const fetchDeviceTelemetry = createServerFn({ method: 'GET' })
  .inputValidator((input: { deviceId: string; params?: ListTelemetryParams }) => input)
  .handler(
    withLog('fetchDeviceTelemetry', async ({ data: { deviceId, params } }) => {
      const headers = await authHeaders()
      const res = await listTelemetryApi(deviceId, params, { headers })
      return unwrap<import('../api/generated/schemas').TelemetryResponse>(
        'fetchDeviceTelemetry',
        res,
      )
    }),
  )

export const bulkUpdateDevices = createServerFn({ method: 'POST' })
  .inputValidator((input: BulkUpdateRequest) => input)
  .handler(
    withLog('bulkUpdateDevices', async ({ data: body }) => {
      const headers = await authHeaders()
      const res = await bulkUpdateApi(body, { headers })
      return unwrap<import('../api/generated/schemas').BulkUpdateResponse>('bulkUpdateDevices', res)
    }),
  )

// --- Sites ---

/* Site address/operatingHours/configOverrides are free-form JSON maps — like
   event payloads, they cross the server-fn boundary as strings. */

export type SerializedSite = Omit<
  SiteView,
  'address' | 'operatingHours' | 'configOverrides'
> & { address: string; operatingHours: string; configOverrides: string }

export type SerializedSiteListResponse = Omit<SiteListResponse, 'items'> & {
  items: SerializedSite[]
}

const serializeSite = (s: SiteView): SerializedSite => ({
  ...s,
  address: JSON.stringify(s.address),
  operatingHours: JSON.stringify(s.operatingHours),
  configOverrides: JSON.stringify(s.configOverrides),
})

export type SiteInput = {
  name: string
  code: string | null
  timezone: string | null
  contactEmail: string | null
  address: string
  operatingHours: string
  configOverrides: string
}

function parseSiteJson(input: SiteInput) {
  return {
    name: input.name,
    code: input.code,
    timezone: input.timezone,
    contactEmail: input.contactEmail,
    address: JSON.parse(input.address) as Record<string, unknown>,
    operatingHours: JSON.parse(input.operatingHours) as Record<string, unknown>,
    configOverrides: JSON.parse(input.configOverrides) as Record<string, unknown>,
  }
}

export const fetchSites = createServerFn({ method: 'GET' })
  .inputValidator((input: ListSitesParams) => input)
  .handler(
    withLog('fetchSites', async ({ data: params }): Promise<SerializedSiteListResponse> => {
      const headers = await authHeaders()
      const res = await listSitesApi(params, { headers })
      const data = unwrap<SiteListResponse>('fetchSites', res)
      return { ...data, items: data.items.map(serializeSite) }
    }),
  )

export const createSite = createServerFn({ method: 'POST' })
  .inputValidator((input: SiteInput) => input)
  .handler(
    withLog('createSite', async ({ data: input }): Promise<SerializedSite> => {
      const headers = await authHeaders()
      const res = await createSiteApi(parseSiteJson(input), { headers })
      return serializeSite(unwrap<SiteView>('createSite', res))
    }),
  )

export const updateSite = createServerFn({ method: 'POST' })
  .inputValidator((input: { siteId: string; body: SiteInput }) => input)
  .handler(
    withLog('updateSite', async ({ data: { siteId, body } }): Promise<SerializedSite> => {
      const headers = await authHeaders()
      const res = await updateSiteApi(siteId, parseSiteJson(body), { headers })
      return serializeSite(unwrap<SiteView>('updateSite', res))
    }),
  )

export const deleteSite = createServerFn({ method: 'POST' })
  .inputValidator((input: { siteId: string }) => input)
  .handler(
    withLog('deleteSite', async ({ data: { siteId } }) => {
      const headers = await authHeaders()
      const res = await deleteSiteApi(siteId, { headers })
      if (res.status !== 204) unwrap('deleteSite', res)
      return { deleted: true }
    }),
  )



// ── Box matrix ────────────────────────────────────────────────────
// Which lock channel on a unit drives which physical box. Authored here and
// pushed with `boxes_sync`, which replaces the device's table wholesale. The
// agent refuses to open any channel absent from its table, so a device that
// has never been synced opens nothing.

export const fetchDeviceBoxes = createServerFn({ method: 'GET' })
  .inputValidator((input: { deviceId: string }) => input)
  .handler(
    withLog('fetchDeviceBoxes', async ({ data: { deviceId } }) => {
      const headers = await authHeaders()
      const res = await listBoxesApi(deviceId, { headers })
      return unwrap<import('../api/generated/schemas').BoxesResponse>('fetchDeviceBoxes', res)
    }),
  )

export const saveDeviceBoxes = createServerFn({ method: 'POST' })
  .inputValidator((input: { deviceId: string; body: BoxesRequest }) => input)
  .handler(
    withLog('saveDeviceBoxes', async ({ data: { deviceId, body } }) => {
      const headers = await authHeaders()
      const res = await replaceBoxesApi(deviceId, body, { headers })
      return unwrap<import('../api/generated/schemas').BoxesResponse>('saveDeviceBoxes', res)
    }),
  )

export const syncDeviceBoxes = createServerFn({ method: 'POST' })
  .inputValidator((input: { deviceId: string }) => input)
  .handler(
    withLog('syncDeviceBoxes', async ({ data: { deviceId } }) => {
      const headers = await authHeaders()
      const res = await syncBoxesApi(deviceId, { headers })
      return unwrap<import('../api/generated/schemas').CommandResponse>('syncDeviceBoxes', res)
    }),
  )

// ── Pickup codes ──────────────────────────────────────────────────
// The codes a unit honours with its uplink down. The API holds the signing
// key; the device verifies against the public half baked into its image.
//
// Two properties to preserve at this boundary: the plaintext code comes back
// exactly once (from `issueDeviceCode`, nowhere else), and revoking republishes
// the whole live set — a device only stops honouring a code when a sync arrives
// without it, so a failed publish is a real failure, not a cosmetic one.

export const fetchDeviceCodes = createServerFn({ method: 'GET' })
  .inputValidator((input: { deviceId: string }) => input)
  .handler(
    withLog('fetchDeviceCodes', async ({ data: { deviceId } }) => {
      const headers = await authHeaders()
      const res = await listCodesApi(deviceId, { headers })
      return unwrap<Array<import('../api/generated/schemas').PickupCodeView>>(
        'fetchDeviceCodes',
        res,
      )
    }),
  )

export const issueDeviceCode = createServerFn({ method: 'POST' })
  .inputValidator((input: { deviceId: string; body: IssueCodeRequest }) => input)
  .handler(
    withLog('issueDeviceCode', async ({ data: { deviceId, body } }) => {
      const headers = await authHeaders()
      const res = await issueCodeApi(deviceId, body, { headers })
      return unwrap<import('../api/generated/schemas').IssuedCodeResponse>('issueDeviceCode', res)
    }),
  )

export const revokeDeviceCode = createServerFn({ method: 'POST' })
  .inputValidator((input: { deviceId: string; codeId: string }) => input)
  .handler(
    withLog('revokeDeviceCode', async ({ data: { deviceId, codeId } }) => {
      const headers = await authHeaders()
      const res = await revokeCodeApi(deviceId, codeId, { headers })
      return unwrap<import('../api/generated/schemas').PickupCodeView>('revokeDeviceCode', res)
    }),
  )

export const syncDeviceCodes = createServerFn({ method: 'POST' })
  .inputValidator((input: { deviceId: string }) => input)
  .handler(
    withLog('syncDeviceCodes', async ({ data: { deviceId } }) => {
      const headers = await authHeaders()
      const res = await syncCodesApi(deviceId, { headers })
      return unwrap<import('../api/generated/schemas').CommandResponse>('syncDeviceCodes', res)
    }),
  )

/** The public key an operator bakes into a device image as CODE_SIGNING_KEY. */
export const fetchCodeSigningKey = createServerFn({ method: 'GET' })
  .handler(
    withLog('fetchCodeSigningKey', async () => {
      const headers = await authHeaders()
      const res = await signingKeyApi({ headers })
      return unwrap<import('../api/generated/schemas').SigningKeyResponse>(
        'fetchCodeSigningKey',
        res,
      )
    }),
  )

// --- Board layout ---

export const fetchDeviceBoards = createServerFn({ method: 'GET' })
  .inputValidator((input: { deviceId: string }) => input)
  .handler(
    withLog('fetchDeviceBoards', async ({ data: { deviceId } }) => {
      const headers = await authHeaders()
      const res = await listBoardsApi(deviceId, { headers })
      return unwrap<import('../api/generated/schemas').BoardsResponse>('fetchDeviceBoards', res)
    }),
  )

export const saveDeviceBoards = createServerFn({ method: 'POST' })
  .inputValidator((input: { deviceId: string; body: BoardsRequest }) => input)
  .handler(
    withLog('saveDeviceBoards', async ({ data: { deviceId, body } }) => {
      const headers = await authHeaders()
      const res = await replaceBoardsApi(deviceId, body, { headers })
      return unwrap<import('../api/generated/schemas').BoardsResponse>('saveDeviceBoards', res)
    }),
  )
